<template>
  <div class="project-key-dates-tab">
    <div v-if="keyDateCount > 0">
      <div v-for="(row, index) in keyDates" :key="index" class="key-dates-section">
        <div v-for="(lease, leaseIndex) in row.leases" :key="leaseIndex" class="key-dates-lease-card">
          <!-- Compact Site and Lease Header -->
          <div class="key-dates-compact-header">
            <img 
              v-if="row.site.image && row.site.image.view_url" 
              :src="row.site.image.view_url" 
              :alt="row.site.building.address_line_1"
              class="key-dates-site-thumbnail"
              style="float: left; margin-right: 2%; width: 32px !important; height: 32px !important; max-width: 32px !important; max-height: 32px !important;"
            />
            <div v-else class="key-dates-site-thumbnail-placeholder" style="width: 32px !important; height: 32px !important;">
              <theme-icon icon="home" type="icons" classes="w-4 h-4" />
            </div>
            <div class="key-dates-header-info">
              <a :href="row.site.view_url" class="key-dates-site-address">
                {{ row.site.building.address_line_1 }}
              </a>
              <span class="key-dates-site-city">
                {{ row.site.building.city }}, {{ row.site.building.state }}
              </span>
            </div>
            <div class="key-dates-lease-label">{{ lease.name }}</div>
          </div>

          <!-- Key Dates Table -->
          <themed-table
            v-if="lease.dates && lease.dates.length"
            :data="lease.dates"
            :columns="tableColumns"
            :hoverable="true"
            aria-label="Key Dates Table"
          >
            <!-- Status with conditional badges -->
            <template #column-status="{ row }">
              <themed-badge
                v-if="!row.completed_at"
                variant="destructive"
              >
                INCOMPLETE
              </themed-badge>
              <themed-badge
                v-else
                variant="success"
              >
                COMPLETED
              </themed-badge>
            </template>
            
            <!-- Note with icon -->
            <template #column-note="{ row }">
              <span v-if="row.note" :title="row.note" class="key-dates-note-icon">
                <theme-icon icon="document-text" type="icons" classes="w-5 h-5" />
              </span>
            </template>
          </themed-table>
          <div v-else class="key-dates-empty">
            No Key Dates Found for this lease
          </div>
        </div>
      </div>
    </div>
    <div v-else class="key-dates-empty-state">
      <theme-icon icon="calendar" type="icons" classes="w-16 h-16" />
      <p class="key-dates-empty-message">No Key Dates Found</p>
    </div>
  </div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ProjectKeyDatesTab',
  
  components: {
    ThemedTable,
    ThemedBadge,
    ThemeIcon
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    },
    sites: {
      type: Array,
      default: () => []
    }
  },
  
  computed: {
    /**
     * Table column configuration for ThemedTable
     */
    tableColumns() {
      return [
        {
          field: 'lease_clause',
          label: 'CLAUSE',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'occurs_on',
          label: 'OCCURS ON',
          type: 'text',
          sortable: true,
          formatter: this.formatDate
        },
        {
          field: 'type',
          label: 'TYPE',
          type: 'text',
          sortable: true,
          searchable: true
        },
        {
          field: 'status',
          label: 'STATUS',
          type: 'text',
          sortable: false,
          width: '120px'
        },
        {
          field: 'note',
          label: 'NOTE',
          type: 'text',
          sortable: false,
          width: '80px'
        }
      ];
    },
    
    /**
     * Filter out archived sites
     */
    activeSites() {
      return this.sites.filter(site => !site.archived_at);
    },
    
    /**
     * Transform sites data into key dates structure
     */
    keyDates() {
      return this.activeSites.map(site => {
        return {
          site: site,
          leases: (site.leases || []).map(lease => {
            return {
              name: lease.name,
              dates: lease.key_dates || []
            };
          })
        };
      }).filter(row => row.leases.length > 0);
    },
    
    /**
     * Count total key dates across all sites and leases
     */
    keyDateCount() {
      return this.keyDates.reduce((total, row) => {
        return total + row.leases.reduce((leaseTotal, lease) => {
          return leaseTotal + (lease.dates?.length || 0);
        }, 0);
      }, 0);
    }
  },
  
  methods: {
    /**
     * Format date for display
     * @param {String} date - Date string
     * @returns {String} Formatted date
     */
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      });
    }
  }
};
</script>
